import { Clipboard } from '@capacitor/clipboard';
import { Share } from '@capacitor/share';

/**
 * Sharing text and a link (a lawn from the builder): the system share sheet where there is one
 * (the native apps, and browsers with the Web Share API), else the link copied to the clipboard.
 * The Capacitor plugins use the browser's APIs on the web.
 */
export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/** iOS's share sheet reports its Copy action like any other: that is a copy, not a share. */
const COPY_ACTIVITY = 'com.apple.UIKit.activity.CopyToPasteboard';

export async function shareLink(title: string, text: string, url: string): Promise<ShareOutcome> {
  try {
    if ((await Share.canShare()).value) {
      const { activityType } = await Share.share({ title, text, url, dialogTitle: title });
      return activityType === COPY_ACTIVITY ? 'copied' : 'shared';
    }
  } catch (e) {
    // Closing the share sheet rejects: that is not a failure.
    if (/cancel/i.test(e instanceof Error ? e.message : String(e))) return 'cancelled';
  }
  try {
    await Clipboard.write({ string: `${text} ${url}` });
    return 'copied';
  } catch {
    return 'failed';
  }
}

/** Shares plain text (a save code): the share sheet where there is one, else the clipboard. */
export async function shareText(title: string, text: string): Promise<ShareOutcome> {
  try {
    if ((await Share.canShare()).value) {
      await Share.share({ title, text, dialogTitle: title });
      return 'shared';
    }
  } catch (e) {
    if (/cancel/i.test(e instanceof Error ? e.message : String(e))) return 'cancelled';
  }
  try {
    await Clipboard.write({ string: text });
    return 'copied';
  } catch {
    return 'failed';
  }
}

/** The clipboard's text (the system may ask the player first), or null. */
export async function pasteText(): Promise<string | null> {
  try {
    const { value } = await Clipboard.read();
    return value || null;
  } catch {
    return null;
  }
}
